// useOtpSession (SIM-7 / ACS-4131, Phase 1 send + Phase 4 session state).
//
// Owns the OTP session for the new /signin flow: the send/resend network calls
// (via the mfaActions.triggerOtp thunk — service layer per ADR-SIM7-002), the 60s
// resend cooldown, the 3-resend cap (the initial send is NOT counted — BR-025), a
// drift-safe countdown derived from a cooldown END TIMESTAMP (not a per-tick
// decrement, so a backgrounded tab / refresh recovers the correct remaining time),
// and the limit message once the cap is hit.
//
// Keyed by securityToken + mfaMode ('phone' | 'email'); there is NO otpId
// (ADR-SIM7-001). Counters persist across a method switch via reset() — BR-041.
//
// The dispatch goes through the Store singleton (not useDispatch) because Auth
// hooks run outside a react-redux Provider in their tests — the established Auth
// pattern (mirrors useAuthFlow).
import { useCallback, useEffect, useState } from 'react';
import store from 'Store';
import MfaActions from 'actions/mfaActions';

// Fire the send network call through the service-layer thunk. The SIM-7 sign-in
// flow no longer wraps this with the GLOBAL interstice decorator (white 75% scrim):
// per Figma 270-85083 the in-flight loading state is the DARK-scrim (rgba(0,0,0,.25))
// shared Loader driven by SignIn.f.jsx's `busy` state around send/verify/redirect
// (FIX 3). Wrapping here too would show BOTH loaders, so withInterstice is removed.
// Defined at module scope — the store.dispatch call inside stays SSR-safe because it
// only runs when invoked.
const dispatchSend = (securityToken, mfaMode) => store.dispatch(MfaActions.triggerOtp(securityToken, mfaMode));

// BR-025 business constants.
const RESEND_COOLDOWN_MS = 60000; // 60s cooldown between sends
const MAX_RESENDS = 3; // resend cap (initial send not counted)
const MS_PER_SECOND = 1000;
const COUNTDOWN_TICK_MS = 1000;

/**
 * OTP session hook for the new /signin flow (SIM-7 / ACS-4131).
 *
 * Owns send/resend (via the mfaActions.triggerOtp thunk — ADR-SIM7-002), the 60s
 * resend cooldown, the 3-resend cap (initial send not counted — BR-025), a
 * drift-safe countdown derived from a cooldown END timestamp, and the limit
 * message. Keyed by securityToken + mfaMode ('phone' | 'email'); counters persist
 * across a method switch via reset() (BR-041). See the module header for the
 * interstice/loader and SSR notes.
 *
 * @returns {{ sendInitial: Function, resend: Function, reset: Function,
 *   otpSession: Object, remainingCooldown: number, limitReached: boolean }}
 */
const useOtpSession = () => {
    const [otpSession, setOtpSession] = useState({
        securityToken: null,
        method: null,
        mfaMode: null,
        maskedDestination: null,
        resendCount: 0,
        cooldownEndTime: 0
    });
    // Ticking clock so the derived countdown re-renders each second while a
    // cooldown is active; the VALUE is always computed from cooldownEndTime.
    const [now, setNow] = useState(() => Date.now());

    const { cooldownEndTime, resendCount } = otpSession;
    const remainingCooldown = Math.max(0, Math.ceil((cooldownEndTime - now) / MS_PER_SECOND));
    const limitReached = resendCount >= MAX_RESENDS;

    // Drive the countdown from a timestamp: tick `now` every second while a
    // cooldown is running so remainingCooldown decrements; the timer lives in an
    // effect only (SSR-safe). Deps are [cooldownEndTime] ONLY — `now` is written
    // from inside the interval, so including it would tear down and recreate the
    // interval every tick. The interval self-clears once the cooldown elapses.
    useEffect(() => {
        if (cooldownEndTime <= Date.now()) {
            return undefined;
        }

        const intervalId = setInterval(() => {
            const tickInstant = Date.now();
            setNow(tickInstant);

            if (cooldownEndTime <= tickInstant) {
                clearInterval(intervalId);
            }
        }, COUNTDOWN_TICK_MS);

        return () => clearInterval(intervalId);
    }, [cooldownEndTime]);

    // Initial send: starts the cooldown but does NOT count toward the cap (BR-025).
    //
    // BR-041: this send fires both for a brand-new session AND on mount after a
    // method-switch reset() (SignIn.f.jsx seeds via reset() then OtpEntryScreen's
    // mount effect calls sendInitial). It must NOT clobber the counters reset()
    // preserved. We key on securityToken: a DIFFERENT token means a fresh account
    // session (zero the counters + start a full cooldown); the SAME token means a
    // channel switch within the same account, so resendCount and cooldownEndTime
    // (and any limit state) are carried forward unchanged.
    const sendInitial = useCallback(async session => {
        const { securityToken, mfaMode, maskedDestination } = session;
        // `method` and `mfaMode` are canonically identical in this flow (see
        // SignIn.f.jsx). OtpEntryScreen calls sendInitial(authContext), and
        // authContext carries only `mfaMode` (no `method`), so derive `method`
        // from `mfaMode` — otherwise otpSession.method would be written as
        // undefined and the persisted refresh-restore (useOtpSessionRecovery,
        // PERSIST_FIELDS.method) would come back undefined, breaking the OTP-step
        // restore (wrong channel copy/credentialType).
        const method = session.method ?? mfaMode;

        await dispatchSend(securityToken, mfaMode);

        const startInstant = Date.now();
        setNow(startInstant);
        setOtpSession(prev => {
            const isSameAccountSession = prev.securityToken != null && prev.securityToken === securityToken;

            if (isSameAccountSession) {
                // Method switch (BR-041): keep the preserved counters/cooldown/limit;
                // only the channel-specific fields change.
                return {
                    ...prev,
                    method,
                    mfaMode,
                    maskedDestination
                };
            }

            // Brand-new session: reset the cap counter and start a fresh cooldown.
            return {
                securityToken,
                method,
                mfaMode,
                maskedDestination,
                resendCount: 0,
                cooldownEndTime: startInstant + RESEND_COOLDOWN_MS
            };
        });
    }, []);

    // Resend: blocked during an active cooldown (AC-027) and once the cap is hit
    // (BR-025). Otherwise increments the counter, sends, and restarts the cooldown.
    const resend = useCallback(async () => {
        const nowInstant = Date.now();

        if (nowInstant < otpSession.cooldownEndTime) {
            // Still inside the cooldown window — ignore.
            return;
        }

        if (otpSession.resendCount >= MAX_RESENDS) {
            // Cap reached (BR-025) — no send. The limit COPY is localized in the
            // screen (getText('otpLimitMessage'), gated on the derived limitReached
            // flag); the hook only tracks the count, never the display string.
            return;
        }

        if (!otpSession.securityToken || !otpSession.mfaMode) {
            return;
        }

        await dispatchSend(otpSession.securityToken, otpSession.mfaMode);

        const nextCount = otpSession.resendCount + 1;
        const startInstant = Date.now();
        setNow(startInstant);
        setOtpSession(prev => ({
            ...prev,
            resendCount: nextCount,
            cooldownEndTime: startInstant + RESEND_COOLDOWN_MS
        }));
    }, [otpSession]);

    // Method switch: swap method/mfaMode/maskedDestination but PRESERVE the
    // resend counter and cooldown end-time (BR-041).
    const reset = useCallback(({ method, mfaMode, maskedDestination }) => {
        setOtpSession(prev => ({
            ...prev,
            method,
            mfaMode,
            ...(maskedDestination !== undefined && { maskedDestination })
        }));
    }, []);

    return {
        sendInitial,
        resend,
        reset,
        otpSession,
        remainingCooldown,
        limitReached
    };
};

export default useOtpSession;
